import moment from 'moment';
import React, { useContext, useEffect, useState } from 'react';
import { Collapse } from 'react-collapse';
import { Agreement, getAgreementHref, normalizeAgreementKey } from '../../../models/Agreement';
import { AgreementMetadata, AgreementStatus } from '../../../models/AgreementMetadata';
import { AgreementStatusIndicator } from '../../agreement/agreement_status';
import { Button } from '../../core/button';
import Dropdown from '../../core/dropdown';
import FontAwesomeIcon from '../../core/fa_icon';
import Notification from '../../core/notification';
import { AgreementActionConfirmation } from './AgreementActionConfirmation';
import { AgreementOrderIndicator } from './AgreementOrderIndicator';
import styles from './agreement_list.module.scss';
import { AgreementVisitsReferrer } from './agreement_visits/AgreementVisitsReferrer';
import { cancelAgreement as cancelAgreementService, deleteAgreement as deleteAgreementService } from './service';
import { attachQcRecording, createOrder as createOrderService, updateAgreementMetadata, uploadOrderFile } from '../../order/service';
import OrderPayload from '../../../models/OrderPayload';
import { SalesContext } from '../../../SalesContext';
import { localizedJoin } from '../../../utils/strings';
import escapeRegexpString from 'escape-string-regexp';
import { sleep } from '../../../utils';
import { fetchAgreementMetadata } from '../../sign_agreement/service';

type Props = {
  agreement: Agreement;
  query?: string;
  /**
   * Whether to animate collapse.
   * May be laggy with many items.
   */
  animate?: boolean;
  onRevise?: (agreement: Agreement, meta: AgreementMetadata) => void;
  onCancel?: (agreement: Agreement, meta: AgreementMetadata) => void;
  onDelete?: (agreement: Agreement, meta: AgreementMetadata) => void;
  onOrderCreated?: (agreement: Agreement, meta: AgreementMetadata, latestOrderId: string) => void;
}

/**
 * Renders a single document list item.
 */
export const AgreementListItem: React.FC<Props> = ({
  agreement,
  query,
  animate = true,
  onRevise,
  onCancel,
  onDelete,
  onOrderCreated
}) => {
  const { products } = useContext(SalesContext);
  const [meta, setMeta] = useState<AgreementMetadata>(null);
  const [overflowVisible, setOverflowVisible] = useState(false);
  const [visitsOpen, setVisitsOpen] = useState(false);

  const [createOrderBusy, setCreateOrderBusy] = useState(false);
  const [createOrderStage, setCreateOrderStage] = useState<'createOrder' | 'uploadFile' | 'updateMeta'>(null);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [isDeleted, setDeleted] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [additionalActionsOpen, setAdditionalActionsOpen] = useState(false);

  const [sensitiveActionDisabled, setSensitiveActionDisabled] = useState(false);

  const isCanceled = [AgreementStatus.Canceled, AgreementStatus.Declined, AgreementStatus.Revised].includes(meta?.status);

  const queryRegexp = query?.trim() ? new RegExp(escapeRegexpString(query), 'i') : null;
  const isMetaQueried = queryRegexp?.test(meta?.creditCheckResult?.fullName);
  const isQueried = queryRegexp?.test(agreement.key) || isMetaQueried || !queryRegexp;

  const { config, offerUri, monthlyPaymentTotal } = useContext(SalesContext);

  async function fetchMeta() {
    try {
      setMeta(await fetchAgreementMetadata(agreement.key));
    } catch (e) {
      console.error(e);
      await sleep(5500);
    }
  }

  /**
   * Creates a SaleSys order.
   */
  async function createOrder() {
    if (createOrderBusy) {
      return;
    }
    setCreateOrderBusy(true);
    setCreateOrderStage('createOrder');

    let orderBundles = meta.bundles;

    try {
      var {orderId, serialId, fileToken} = await createOrderService(new OrderPayload({
        creditCheckResult: meta.creditCheckResult,
        agreement,
        bundles: orderBundles,
        agreementMeta: meta
      }), config);
    } catch (e) {
      console.error(e);
      Notification.show("Kunde inte lägga order");
    }

    if (orderId) {
      Notification.show(`Lagt order ${serialId}`);
    }

    setCreateOrderStage('uploadFile');
    await attachQcRecording(agreement.key, orderId, fileToken)
      .catch(error => {
        console.error("Error attaching agreement %s QC recording (via list)", agreement.key, error);
      });

    try {
      await Promise.all([
        uploadOrderFile(orderId, fileToken, {
          fileUrl: agreement.uri,
          fileName: 'Avtal.pdf'
        })
      ])
      Notification.show(`Bifogat avtal i order ${serialId}`);
    } catch (e) {
      console.error(e);
      Notification.show(`Kunde inte bifoga avtal i order ${serialId}`);
    }


    setCreateOrderStage('updateMeta');
    if (orderId) {
      try {
        await updateAgreementMetadata(agreement.key, { orderId, orderSerialId: serialId });
      } catch (e) {
        console.error(e);
        Notification.show(`Kunde inte koppla order ${serialId} till avtalet`);

        setCreateOrderBusy(false);
        return;
      }

      onOrderCreated?.(agreement, meta, orderId);
    }

    setCreateOrderStage(null);
    setCreateOrderBusy(false);
  }

  /**
   * Cancels this agreement.
   */
  async function cancelAgreement() {
    if (cancelBusy) {
      return;
    }
    setCancelBusy(true);

    let success = false;

    try {
      await cancelAgreementService(agreement.key);
      success = true;
    } catch (e) {
      console.error(e);
      Notification.show("Kunde inte återkalla avtalet, prova igen senare");
    }

    if (success) {
      setMeta(meta => new AgreementMetadata({ ...meta, status: AgreementStatus.Canceled }));

      Notification.show("Återkallat avtalet");
      onCancel?.(agreement, meta);
    }

    setCancelConfirmOpen(false);
    setCancelBusy(false);
  }

  async function deleteAgreement() {
    if (deleteBusy) {
      return;
    }
    setDeleteBusy(true);

    let success = false;

    try {
      await deleteAgreementService(agreement.key);
      success = true;
    } catch (e) {
      console.error(e);
      Notification.show("Kunde inte radera avtalet, prova igen senare");
    }

    if (success) {
      Notification.show("Raderat avtalet");
      setDeleted(true);
      setTimeout(() => onDelete?.(agreement, meta), 500);
    }

    setDeleteBusy(false);
  }

  useEffect(() => {
    if (cancelConfirmOpen || deleteConfirmOpen) {
      setSensitiveActionDisabled(true);
    }
  }, [cancelConfirmOpen, deleteConfirmOpen]);

  useEffect(() => {
    if (sensitiveActionDisabled) {
      setTimeout(() => setSensitiveActionDisabled(false), 1500);
    }
  }, [sensitiveActionDisabled]);

  useEffect(() => {
    if (agreement.key) {
      fetchMeta();
    }
  }, [agreement.key]);

  const memorableAgreementKey = normalizeAgreementKey(agreement.key).substr(0, 6);

  useEffect(() => {
    if (visitsOpen) {
      setOverflowVisible(true);
    } else {
      setTimeout(() => setOverflowVisible(false), 350);
    }
  }, [visitsOpen]);

  const content = (
    <div className={styles.item} style={{ overflow: overflowVisible ? 'visible' : null }} role="row">
      <div>
        {formatDateTime(meta?.createdAt)}
      </div>
      <div>
        <span>{meta?.creditCheckResult?.fullName}</span>
      </div>
      <div>
        <AgreementStatusIndicator
          className={styles.status}
          agreement={agreement}
          meta={meta}
          link={false}
          prefix={false}
          offerUri={offerUri}
          />
      </div>
      <div>
        <AgreementVisitsReferrer
          meta={meta}
          open={visitsOpen}
          onOpen={() => setVisitsOpen(true)}
          onClose={() => setVisitsOpen(false)} />
      </div>
      <div className={styles.documents}>
        <a href={getAgreementHref(agreement.key)} rel="noreferrer noopener" target="_blank" title="Öppna PDF">
          <FontAwesomeIcon name="file-pdf" className={styles.icon} />
        </a>

        {
          meta?.orderId ? (
            <a href={`https://app.salesys.se/orders/${meta.orderId}`} rel="noreferrer noopener" target="_blank" title="Öppna order">
              <AgreementOrderIndicator />
            </a>
          ) : null
        }
      </div>
      <div className={styles.actions}>
        {
          isCanceled ? (
            <Button
              onClick={() => setDeleteConfirmOpen(true)}>
              Radera
            </Button>
          ) : (
            <Button
              onClick={() => setCancelConfirmOpen(true)}
              disabled={!meta?.bundles || !meta?.creditCheckResult}>
              Återkalla
            </Button>
          )
        }
        <div style={{ position: 'relative' }}>
          <Button weight="simple" onClick={() => setAdditionalActionsOpen(true)}>
            <FontAwesomeIcon name="ellipsis-v" />
          </Button>

          <Dropdown
            open={additionalActionsOpen || createOrderBusy}
            onRequestHide={() => setAdditionalActionsOpen(false)}
            items={[
              {
                content: 'Öppna PDF',
                href: getAgreementHref(agreement.key)
              },
              {
                disabled: createOrderBusy || (!meta?.orderId && (![AgreementStatus.Signed].includes(meta?.status) || !meta?.bundles.length)),
                href: meta?.orderId ? `https://app.salesys.se/orders/${meta.orderId}` : null,
                content: meta?.orderId ? (
                  meta.bundles?.length > 1 ? 'Visa senaste order' : 'Visa order'
                ) : createOrderStage === 'createOrder' ? (
                  meta?.bundles?.length > 1 ? `Lägger ${meta.bundles.length} ordrar` : 'Lägger order'
                ) : createOrderStage === 'uploadFile' ? (
                  'Bifogar avtal'
                ) : createOrderStage === 'updateMeta' ? (
                  'Kopplar order'
                ) : (
                  meta?.bundles?.length > 1 ? `Lägg ${meta.bundles.length} ordrar` : 'Lägg order'
                ),
                busy: createOrderBusy,
                onClick: () => {
                  if (!meta?.orderId) {
                    createOrder();
                  }

                  setAdditionalActionsOpen(false)
                }
              }
            ]}
            align="right"
          />
        </div>
      </div>

      {
        isCanceled ? (
          <AgreementActionConfirmation visible={deleteConfirmOpen}>
            <p style={{ flexGrow: 1 }}>
              Vill du radera avtalet som börjar på <strong>{memorableAgreementKey}</strong>? Detta är permanent.
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                weight="simple-dark"
                disabled={sensitiveActionDisabled}
                onClick={() => deleteAgreement()}
                busy={deleteBusy}>
                Radera
              </Button>
              <Button weight="primary" onClick={() => setDeleteConfirmOpen(false)}>
                Avbryt
              </Button>
            </div>
          </AgreementActionConfirmation>
        ) : (
          <AgreementActionConfirmation visible={cancelConfirmOpen}>
            <p style={{ flexGrow: 1 }}>
              Vill du återkalla avtalet som börjar på <strong>{memorableAgreementKey}</strong>? Detta är permanent.
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                weight="simple-dark"
                disabled={sensitiveActionDisabled}
                onClick={() => cancelAgreement()}
                busy={cancelBusy}>
                Återkalla
              </Button>
              <Button weight="primary" onClick={() => setCancelConfirmOpen(false)}>
                Avbryt
              </Button>
            </div>
          </AgreementActionConfirmation>
        )
      }
    </div>
  );

  if (animate) {
    return (
      <div className={styles.itemContainer}>
        <Collapse isOpened={isQueried && !isDeleted}>
          {content}
        </Collapse>
      </div>
    )
  }

  if (!isQueried || isDeleted) {
    return null;
  }

  return (
    <div className={styles.itemContainer}>
      {content}
    </div>
  )
}

function formatDateTime(date: Date) {
  if (!date) {
    return null;
  }

  const formattedTime = moment(date).format('HH:mm');

  const dayDiff = moment.duration(moment(date).startOf('day').diff(moment().startOf('day'))).asDays();

  if (dayDiff === -1) {
    return `igår ${formattedTime}`;
  }

  if (dayDiff === 0) {
    return `idag ${formattedTime}`;
  }

  return `${moment(date).format('YYYY-MM-DD')} ${formattedTime}`;
}